'use client';

import { usePathname } from 'next/navigation';
import { SearchPaginationKeys } from '@/constants/listing-search-keys';
import {
  DefaultAdults,
  DefaultChildren,
  MaxGuests,
  useSearch,
} from '@/context/listing-search-provider';
import { clearGuestSearchParams } from '@/utils/guest-search-params';
import { Listing } from '@/utils/platform-api/__generated__/graphql';
import {
  formatDateParamForDisplay,
  isValidDateParam,
} from '@/utils/parse-safe-date';
import clsx from 'clsx';
import { ClientBrandConfig } from 'types/brand-config';

import { formatGuestsLabel } from '@/components/search/utils/format-guests-label';

import DateRangePopoverWrapper from './date-range-popover-wrapper';
import GuestPickerPopover from './guest-picker-popover';

interface AvailabilityInputsProps {
  searchParams?: {
    arrive?: string;
    depart?: string;
    guests?: string;
  };
  error?: string;
  onClearDates: () => void;
  brand: ClientBrandConfig;
  listing: Listing;
}
const AvailabilityInputs = ({
  error: errorProp,
  onClearDates,
  searchParams,
  brand,
  listing,
}: AvailabilityInputsProps) => {
  const routerPathname = usePathname();
  const {
    clearDateParams,
    setAdults,
    setChildren,
    navigate,
    searchParams: urlSearchParams,
    adults: adultsFromContext,
    children: childrenFromContext,
  } = useSearch();
  const adults = adultsFromContext ?? DefaultAdults;
  const children = childrenFromContext ?? DefaultChildren;
  const arrive = isValidDateParam(searchParams?.arrive) ? searchParams.arrive : '';
  const depart = isValidDateParam(searchParams?.depart) ? searchParams.depart : '';
  const guests = adults + children;
  const guestsDisplay = formatGuestsLabel(adults, children);
  const isDefaultGuests = adults === DefaultAdults && children === DefaultChildren;
  const arriveDisplay = formatDateParamForDisplay(searchParams?.arrive);
  const departDisplay = formatDateParamForDisplay(searchParams?.depart);
  const clearDates = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Clear error state when dates are cleared
    onClearDates();
    clearDateParams(true, routerPathname, { scroll: false });
  };

  const clearGuests = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setAdults(undefined);
    setChildren(undefined);
    const current = new URLSearchParams(urlSearchParams.toString());
    current.delete(SearchPaginationKeys.OFFSET);
    clearGuestSearchParams(current);
    navigate(
      `${routerPathname}${current.toString() ? `?${current.toString()}` : ''}`,
      { scroll: false }
    );
  };

  // Use error from props (from useFormState) or check for date validation errors
  const hasArriveError = !arrive && depart;
  const hasDepartError = !depart && arrive;
  const showArriveError =
    errorProp || (hasArriveError ? 'Missing arrive date' : undefined);
  const showDepartError =
    errorProp || (hasDepartError ? 'Missing depart date' : undefined);
  const showGuestError =
    errorProp ||
    (guests > (listing?.max_guests || MaxGuests)
      ? 'Max guests reached'
      : undefined);

  return (
    <div className={clsx('grid grid-cols-2 rounded-lg border divide-y')}>
      <DateRangePopoverWrapper
        className="col-span-2"
        brand={brand}
        listing={listing}
      >
        <div className="flex w-full divide-x">
          <div className="relative flex w-1/2 flex-col p-2 md:p-4">
            <label
              className={clsx(
                'whitespace-nowrap text-xs font-medium leading-5 text-secondary-main group-focus-within:font-semibold group-focus-within:text-primary-main lg:text-xs',
                showArriveError && '!text-error-dark'
              )}
              htmlFor="arrive-display"
            >
              CHECK-IN
            </label>
            <input type="hidden" id="arrive" name="arrive" value={arrive} />
            <input
              type="text"
              id="arrive-display"
              name="arrive-display"
              className="cursor-pointer border-none p-0 font-medium outline-none placeholder:text-xs placeholder:font-normal placeholder:normal-case placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0 placeholder:lg:text-sm"
              value={arriveDisplay}
              placeholder="Select date..."
              readOnly
            />
            {!!arrive && (
              <button
                type="button"
                onClick={clearDates}
                className="absolute right-0 top-1/2 -translate-y-1/2 transform pl-0 pr-[.65rem] text-gray-5 hover:text-gray-6"
              >
                <svg
                  width=".8rem"
                  height=".8rem"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 24C15.1826 24 18.2348 22.7357 20.4853 20.4853C22.7357 18.2348 24 15.1826 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0C8.8174 0 5.76516 1.26428 3.51472 3.51472C1.26428 5.76516 0 8.8174 0 12C0 15.1826 1.26428 18.2348 3.51472 20.4853C5.76516 22.7357 8.8174 24 12 24ZM8.73281 6.39844L12 10.2563L15.2672 6.39844C15.6703 5.925 16.3781 5.86406 16.8516 6.26719C17.325 6.67031 17.3859 7.37813 16.9828 7.85156L13.4719 12L16.9828 16.1484C17.3859 16.6219 17.325 17.3344 16.8516 17.7328C16.3781 18.1312 15.6656 18.075 15.2672 17.6016L12 13.7438L8.73281 17.6016C8.32969 18.075 7.62187 18.1359 7.14844 17.7328C6.675 17.3297 6.61406 16.6219 7.01719 16.1484L10.5281 12L7.01719 7.85156C6.61406 7.37813 6.675 6.66562 7.14844 6.26719C7.62187 5.86875 8.33438 5.925 8.73281 6.39844Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="relative flex w-1/2 flex-col p-2 md:p-4">
            <label
              className={clsx(
                'whitespace-nowrap text-xs font-medium leading-5 text-secondary-main group-focus-within:font-semibold group-focus-within:text-primary-main lg:text-xs',
                showDepartError && '!text-error-dark'
              )}
              htmlFor="depart-display"
            >
              CHECK-OUT
            </label>
            <input type="hidden" id="depart" name="depart" value={depart} />
            <input
              type="text"
              id="depart-display"
              name="depart-display"
              className="cursor-pointer border-none p-0 font-medium outline-none placeholder:text-xs placeholder:font-normal placeholder:normal-case placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0 placeholder:lg:text-sm"
              value={departDisplay}
              placeholder="Select date..."
              readOnly
            />
            {!!depart && (
              <button
                type="button"
                onClick={clearDates}
                className="absolute right-0 top-1/2 -translate-y-1/2 transform pl-0 pr-[.65rem] text-gray-5 hover:text-gray-6"
              >
                <svg
                  width=".8rem"
                  height=".8rem"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 24C15.1826 24 18.2348 22.7357 20.4853 20.4853C22.7357 18.2348 24 15.1826 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0C8.8174 0 5.76516 1.26428 3.51472 3.51472C1.26428 5.76516 0 8.8174 0 12C0 15.1826 1.26428 18.2348 3.51472 20.4853C5.76516 22.7357 8.8174 24 12 24ZM8.73281 6.39844L12 10.2563L15.2672 6.39844C15.6703 5.925 16.3781 5.86406 16.8516 6.26719C17.325 6.67031 17.3859 7.37813 16.9828 7.85156L13.4719 12L16.9828 16.1484C17.3859 16.6219 17.325 17.3344 16.8516 17.7328C16.3781 18.1312 15.6656 18.075 15.2672 17.6016L12 13.7438L8.73281 17.6016C8.32969 18.075 7.62187 18.1359 7.14844 17.7328C6.675 17.3297 6.61406 16.6219 7.01719 16.1484L10.5281 12L7.01719 7.85156C6.61406 7.37813 6.675 6.66562 7.14844 6.26719C7.62187 5.86875 8.33438 5.925 8.73281 6.39844Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </DateRangePopoverWrapper>
      <GuestPickerPopover
        className="col-span-2"
        maxGuests={listing?.max_guests || undefined}
      >
        <div className="group relative flex flex-col p-2 md:p-4">
          <label
            className={clsx(
              'whitespace-nowrap text-xs font-medium leading-5 text-secondary-main group-focus-within:font-semibold group-focus-within:text-primary-main lg:text-xs',
              showGuestError && '!text-error-dark'
            )}
            htmlFor="guests-display"
          >
            GUESTS
          </label>
          <input
            type="text"
            id="guests-display"
            className="cursor-pointer border-none p-0 font-medium outline-none placeholder:text-xs placeholder:font-normal placeholder:normal-case placeholder:text-gray-400 focus:border-none focus:outline-none focus:ring-0 placeholder:lg:text-sm"
            value={guestsDisplay}
            readOnly
          />
          <input type="hidden" id="guests" name="guests" value={guests} />
          <input type="hidden" name="adults" value={adults} />
          <input type="hidden" name="children" value={children} />
          {!isDefaultGuests && (
            <button
              type="button"
              onClick={clearGuests}
              className="absolute right-0 top-1/2 -translate-y-1/2 transform pl-0 pr-[.65rem] text-gray-5 hover:text-gray-6"
            >
              <svg
                width=".8rem"
                height=".8rem"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 24C15.1826 24 18.2348 22.7357 20.4853 20.4853C22.7357 18.2348 24 15.1826 24 12C24 8.8174 22.7357 5.76516 20.4853 3.51472C18.2348 1.26428 15.1826 0 12 0C8.8174 0 5.76516 1.26428 3.51472 3.51472C1.26428 5.76516 0 8.8174 0 12C0 15.1826 1.26428 18.2348 3.51472 20.4853C5.76516 22.7357 8.8174 24 12 24ZM8.73281 6.39844L12 10.2563L15.2672 6.39844C15.6703 5.925 16.3781 5.86406 16.8516 6.26719C17.325 6.67031 17.3859 7.37813 16.9828 7.85156L13.4719 12L16.9828 16.1484C17.3859 16.6219 17.325 17.3344 16.8516 17.7328C16.3781 18.1312 15.6656 18.075 15.2672 17.6016L12 13.7438L8.73281 17.6016C8.32969 18.075 7.62187 18.1359 7.14844 17.7328C6.675 17.3297 6.61406 16.6219 7.01719 16.1484L10.5281 12L7.01719 7.85156C6.61406 7.37813 6.675 6.66562 7.14844 6.26719C7.62187 5.86875 8.33438 5.925 8.73281 6.39844Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          )}
        </div>
      </GuestPickerPopover>
    </div>
  );
};

export default AvailabilityInputs;
